import { useCallback } from "react";
import { trackLoginEvent, LoginEvent } from "@/utils/analytics.utils";

const LOCKED_ERROR_CODES = new Set(["E0000069", "E0000047"]);
const MFA_MISSING_CONTEXT_ERROR = "MFA verification failed: missing factor or state token.";

type VerifyContext = {
  factorId?: string;
  stateToken?: string;
  link?: string;
};

function resolveFactor(factor: any, selectedFactor: any) {
  return factor || selectedFactor;
}

function buildVerifyContext(auth: any, factorToUse: any): VerifyContext {
  return {
    stateToken: auth.mfaState?.stateToken,
    factorId: factorToUse?.selectedFactorId,
    link: factorToUse?.link,
  };
}

function resetVerificationState(
  setIsVerifying: (value: boolean) => void,
  setIsRedirecting: (value: boolean) => void,
) {
  setIsVerifying(false);
  setIsRedirecting(false);
}

function extractErrorCode(err: unknown): string | undefined {
  if (typeof err === "object" && err !== null && "errorCode" in err) {
    return (err as { errorCode?: string }).errorCode;
  }
  return undefined;
}

function isLockedError(errorCode: string | undefined, errorMessage: string): boolean {
  const normalizedMessage = errorMessage.toLowerCase();
  return (
    LOCKED_ERROR_CODES.has(errorCode ?? "") ||
    normalizedMessage.includes("locked") ||
    normalizedMessage.includes("too many attempts")
  );
}

function trackFailureEvent(
  isAccountLockedError: boolean,
  factorType?: string,
) {
  try {
    if (isAccountLockedError) {
      trackLoginEvent(LoginEvent.ACCOUNT_LOCKED);
    } else if (factorType === "email") {
      trackLoginEvent(LoginEvent.OTP_EMAIL_VALIDATION_FAILED);
    } else if (factorType === "sms" || factorType === "phone") {
      trackLoginEvent(LoginEvent.OTP_PHONE_VALIDATION_FAILED);
    }
  } catch {}
}

function resolveErrorMessage(
  isAccountLockedError: boolean,
  errorCode: string | undefined,
  t: (key: string, fallback?: string) => string,
): string {
  if (isAccountLockedError) {
    return t("errorCodes.CUST_S1200002");
  }

  if (errorCode === "E0000068") {
    return t("errors.invalidCode", "Invalid verification code");
  }

  return t("errors.genericError", "Something went wrong. Please try again.");
}

export function useMFAVerify({ auth, selectedFactor, setIsVerifying, setMfaError, setMfaErrorCode, setIsRedirecting, handleSuccessfulLoginRedirect, t }) {
  return useCallback(
    async (code: string, factor: any) => {
      setIsVerifying(true);
      setMfaError(null);
      setMfaErrorCode(null);

      const factorToUse = resolveFactor(factor, selectedFactor);
      const { stateToken, factorId, link } = buildVerifyContext(auth, factorToUse);

      try {
        console.debug('[MFA VERIFY] stateToken:', stateToken);
        console.debug('[MFA VERIFY] factorId:', factorId);
        console.debug('[MFA VERIFY] link:', link);

        if (!factorId || !stateToken) {
          setMfaError(MFA_MISSING_CONTEXT_ERROR);
          resetVerificationState(setIsVerifying, setIsRedirecting);
          return;
        }

        const verifyRequest = { factorId, stateToken, passCode: code, link };
        console.debug('[MFA VERIFY] verifyRequest:', verifyRequest);
        await auth.verifyMFA(verifyRequest);
        setIsVerifying(false);

        try {
          trackLoginEvent(LoginEvent.SUCCESS);
        } catch (trackingError) {}

        setIsRedirecting(true);
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Retrieve the sessionToken from sessionStorage after successful MFA
        const sessionToken = sessionStorage.getItem('okta_session_token');
        console.debug('[MFA VERIFY] sessionToken after verification:', sessionToken);
        handleSuccessfulLoginRedirect(sessionToken);
      } catch (err) {
        const errorCode = extractErrorCode(err);
        const rawErrorMessage = err instanceof Error ? err.message : "";
        const factorType = factorToUse?.factorType;
        const isAccountLockedError = isLockedError(errorCode, rawErrorMessage);
        const userFacingMessage = resolveErrorMessage(
          isAccountLockedError,
          errorCode,
          t,
        );

        trackFailureEvent(isAccountLockedError, factorType);

        setMfaError(userFacingMessage);
        setMfaErrorCode(errorCode ?? null);
        resetVerificationState(setIsVerifying, setIsRedirecting);
        console.error('[MFA VERIFY] Error during verification:', err);
      }
    },
    [auth, selectedFactor, setIsVerifying, setMfaError, setMfaErrorCode, setIsRedirecting, handleSuccessfulLoginRedirect, t]
  );
}
